import cx from 'classnames'
import { useCallback, useEffect, useState } from 'react'
import Styles from './LinkedinPreview.module.scss'
import { MediaRenderer } from '../components/MediaRenderer/MediaRenderer'
import useEmblaCarousel from 'embla-carousel-react'
import { ChevronLeft, ChevronRight } from 'blaze-ui/icons/24'
import { MediaLayoutProps } from './ImageGridMedia'
import { useOptionalCurrentSlide } from 'screens/Calendar/FullScreenPreview/CurrentSlideContext'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'

interface CarouselMediaProps extends MediaLayoutProps {
  carouselTitle?: string
}

export const CarouselMedia = ({ mediaObjects, carouselTitle, ...mediaProps }: CarouselMediaProps) => {
  const [imageIndex, setImageIndex] = useState(0)
  const currentSlideContext = useOptionalCurrentSlide()
  useEffect(() => {
    currentSlideContext?.setCurrentSlideIndex(imageIndex, ExternalIntegrationEnum.LinkedIn)
  }, [imageIndex, currentSlideContext])
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: 'start' })

  const onEmblaSelect = useCallback(() => {
    if (!emblaApi) return
    setImageIndex(emblaApi.selectedScrollSnap())
    setCanScrollPrev(emblaApi.canScrollPrev())
    setCanScrollNext(emblaApi.canScrollNext())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return
    emblaApi.on('select', onEmblaSelect)
    emblaApi.on('reInit', onEmblaSelect)
    onEmblaSelect()
    return () => {
      emblaApi.off('select', onEmblaSelect)
      emblaApi.off('reInit', onEmblaSelect)
    }
  }, [emblaApi, onEmblaSelect])

  return (
    <div className={Styles.carouselWrapper} data-testid="linkedin-carousel">
      <div className={Styles.carouselImageArea}>
        <div className={Styles.carouselHeader}>
          {carouselTitle && <strong>{carouselTitle}</strong>}
          {carouselTitle && ' · '}
          {mediaObjects.length} pages
        </div>
        <div className={Styles.carouselViewport} ref={emblaRef}>
          <div className={Styles.carouselContainer}>
            {mediaObjects.filter(Boolean).map((mediaObject) => {
              const mediaUrl = mediaObject?.thumbnail_url || mediaObject?.source_url
              if (!mediaUrl) return null
              return (
                <div key={mediaUrl} className={Styles.carouselSlide}>
                  <MediaRenderer
                    mediaObject={mediaObject}
                    alt=""
                    className={Styles.imagePreview}
                    showVideoControls={mediaProps.showVideoControls}
                    showHoverActionOverlay={mediaProps.showHoverActionOverlay}
                    almanacFileKey={mediaProps.almanacFileKey}
                    isActive={mediaProps.isActive}
                    isPressable={!mediaProps.showHoverActionOverlay}
                    isGenerating={mediaProps.isGenerating}
                    onClose={mediaProps.onClose}
                    allMediaObjects={mediaObjects}
                  />
                </div>
              )
            })}
          </div>
        </div>
        {canScrollPrev && (
          <button
            className={cx(Styles.carouselArrow, Styles.carouselArrowPrev)}
            onClick={() => emblaApi?.scrollPrev()}
            aria-label="Previous"
          >
            <ChevronLeft size={20} />
          </button>
        )}
        {canScrollNext && (
          <button
            className={cx(Styles.carouselArrow, Styles.carouselArrowNext)}
            onClick={() => emblaApi?.scrollNext()}
            aria-label="Next"
          >
            <ChevronRight size={20} />
          </button>
        )}
        <div className={Styles.carouselFooter}>
          <span className={Styles.carouselCounter}>
            {imageIndex + 1} / {mediaObjects.length}
          </span>
          <div className={Styles.progressBar}>
            <div
              className={Styles.progressFill}
              style={{ width: `${(imageIndex / (mediaObjects.length - 1)) * 100}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
